import { fmtDate } from "@/lib/dateFormat";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Loader2, Plus, Ticket } from "lucide-react";
import { adminApi } from "@/lib/admin-api";

const PLAN_OPTS = [["basic", "Basic"], ["pro", "Pro"], ["premium", "Premium"]];

export default function AdminPromotions() {
  const navigate = useNavigate();
  const [codes, setCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [show, setShow] = useState(false);
  const [msg, setMsg] = useState(null);

  const load = () => { setLoading(true); adminApi("/admin/promo-codes").then((r) => setCodes(r.promo_codes || [])).finally(() => setLoading(false)); };
  useEffect(load, []);

  const toggle = async (c) => { await adminApi(`/admin/promo-codes/${c.id}`, { method: "PATCH" }); load(); };

  return (
    <div className="min-h-screen bg-midnight">
      <div className="mx-auto max-w-5xl px-6 py-8">
        <button onClick={() => navigate("/owner/dashboard")} className="mb-4 inline-flex items-center gap-1 text-sm font-semibold text-brand"><ArrowLeft className="h-4 w-4" />Back to dashboard</button>
        <div className="flex items-center justify-between">
          <h1 className="font-display text-4xl font-bold uppercase text-white">Promotion Codes</h1>
          <button onClick={() => setShow((v) => !v)} data-testid="new-promo-btn" className="inline-flex items-center gap-1.5 rounded-lg bg-brand px-4 py-2 text-sm font-bold text-white"><Plus className="h-4 w-4" />New code</button>
        </div>
        <p className="mt-1 text-sm text-white/50">Codes customers enter at Checkout. Validated server-side and applied via Stripe. Never re-grants the 14-day free trial.</p>
        {msg && <p data-testid="promo-msg" className="mt-3 rounded-lg bg-white/5 px-3 py-2 text-sm text-white/80">{msg}</p>}

        {show && <CreateForm onDone={(m) => { setShow(false); setMsg(m); load(); }} onError={setMsg} />}

        {loading ? <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-brand" /></div> : (
          <div className="mt-6 overflow-hidden rounded-xl border border-white/10">
            <table className="w-full text-left text-sm">
              <thead className="bg-white/5 text-[10px] uppercase tracking-widest text-white/50">
                <tr><th className="px-4 py-3">Code</th><th className="px-4 py-3">Discount</th><th className="px-4 py-3">Eligible</th><th className="px-4 py-3">Redeemed</th><th className="px-4 py-3">Paid</th><th className="px-4 py-3">Expires</th><th className="px-4 py-3">Status</th><th className="px-4 py-3" /></tr>
              </thead>
              <tbody className="divide-y divide-white/5">
                {codes.map((c) => (
                  <tr key={c.id} data-testid={`promo-row-${c.code}`} className="text-white/90">
                    <td className="px-4 py-3 font-bold">{c.code}{c.demo && <span className="ml-1 text-[10px] text-white/40">(demo)</span>}</td>
                    <td className="px-4 py-3">{c.discount_type === "one_month_free" ? "1st month free" : c.discount_type === "percent" ? `${c.value}% off` : `£${c.value} off`}</td>
                    <td className="px-4 py-3 text-white/50">{(c.eligible_plans?.length ? c.eligible_plans.join("/") : "all")} · {(c.eligible_periods?.length ? c.eligible_periods.join("/") : "all")}</td>
                    <td className="px-4 py-3">{c.times_redeemed}{c.max_redemptions ? `/${c.max_redemptions}` : ""}</td>
                    <td className="px-4 py-3">{c.conversions ?? 0}</td>
                    <td className="px-4 py-3 text-white/50">{c.expires_at ? fmtDate(c.expires_at) : "—"}</td>
                    <td className="px-4 py-3"><span className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${c.active ? "bg-success/20 text-success" : "bg-white/10 text-white/50"}`}>{c.active ? "ACTIVE" : "DISABLED"}</span></td>
                    <td className="px-4 py-3 text-right"><button onClick={() => toggle(c)} data-testid={`promo-toggle-${c.code}`} className="rounded-lg border border-white/15 px-3 py-1 text-xs font-bold text-white/80">{c.active ? "Disable" : "Enable"}</button></td>
                  </tr>
                ))}
                {codes.length === 0 && <tr><td colSpan={8} className="px-4 py-10 text-center text-white/40"><Ticket className="mx-auto mb-2 h-6 w-6 text-white/20" />No promotion codes yet.</td></tr>}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

const inputCls = "w-full rounded-lg border border-white/15 bg-white/5 px-3 py-2.5 text-sm text-white outline-none focus:border-brand";
function Lbl({ children }) { return <label className="mb-1 block text-[11px] font-bold uppercase tracking-widest text-white/50">{children}</label>; }

function CreateForm({ onDone, onError }) {
  const [code, setCode] = useState("");
  const [type, setType] = useState("percent");
  const [value, setValue] = useState("");
  const [plans, setPlans] = useState([]);
  const [periods, setPeriods] = useState([]);
  const [expires, setExpires] = useState("");
  const [maxR, setMaxR] = useState("");
  const [busy, setBusy] = useState(false);

  const togglePlan = (p) => setPlans((a) => a.includes(p) ? a.filter((x) => x !== p) : [...a, p]);
  const togglePeriod = (p) => setPeriods((a) => a.includes(p) ? a.filter((x) => x !== p) : [...a, p]);

  const submit = async () => {
    setBusy(true);
    try {
      const body = {
        code: code.trim(), discount_type: type, value: type === "one_month_free" ? 0 : Number(value),
        eligible_plans: plans, eligible_periods: periods,
        expires_at: expires ? new Date(expires + "T23:59:59").toISOString() : null,
        max_redemptions: maxR ? Number(maxR) : null,
      };
      await adminApi("/admin/promo-codes", { method: "POST", body: JSON.stringify(body) });
      onDone(`Code ${body.code.toUpperCase()} created.`);
    } catch (e) { onError(e.message || "Failed to create code"); } finally { setBusy(false); }
  };
  const chip = (on) => `rounded-lg border px-3 py-1.5 text-xs font-bold ${on ? "border-brand bg-brand/10 text-white" : "border-white/15 text-white/60"}`;
  const ready = code.trim().length >= 2 && (type === "one_month_free" || Number(value) > 0);

  return (
    <div className="mt-4 rounded-xl border border-white/10 bg-white/5 p-5">
      <div className="grid gap-3 sm:grid-cols-3">
        <div><Lbl>Code</Lbl><input value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} data-testid="promo-code" className={inputCls} placeholder="WELCOME20" /></div>
        <div><Lbl>Type</Lbl><select value={type} onChange={(e) => setType(e.target.value)} data-testid="promo-type" className={inputCls}><option value="percent">Percentage off</option><option value="fixed">Fixed £ off</option><option value="one_month_free">First month free (100%)</option></select></div>
        {type !== "one_month_free" && <div><Lbl>{type === "percent" ? "Percent" : "£ off"}</Lbl><input value={value} onChange={(e) => setValue(e.target.value)} type="number" data-testid="promo-value" className={inputCls} placeholder={type === "percent" ? "20" : "10"} /></div>}
      </div>
      <div className="mt-3 flex flex-wrap items-center gap-2">
        <span className="text-[11px] font-bold uppercase tracking-widest text-white/40">Plans:</span>
        {PLAN_OPTS.map(([v, l]) => <button key={v} onClick={() => togglePlan(v)} className={chip(plans.includes(v))}>{l}</button>)}
        <span className="ml-3 text-[11px] font-bold uppercase tracking-widest text-white/40">Billing:</span>
        {[["monthly", "Monthly"], ["annual", "Annual"]].map(([v, l]) => <button key={v} onClick={() => togglePeriod(v)} className={chip(periods.includes(v))}>{l}</button>)}
      </div>
      <div className="mt-3 grid gap-3 sm:grid-cols-2">
        <div><Lbl>Expiry (optional)</Lbl><input type="date" value={expires} onChange={(e) => setExpires(e.target.value)} data-testid="promo-expires" className={inputCls} /></div>
        <div><Lbl>Redemption limit (optional)</Lbl><input value={maxR} onChange={(e) => setMaxR(e.target.value)} type="number" data-testid="promo-maxr" className={inputCls} placeholder="e.g. 100" /></div>
      </div>
      <p className="mt-2 text-xs text-white/40">Leave plans/billing empty to allow all. Returning customers can use a code but never receive a second 14-day free trial.</p>
      <button disabled={busy || !ready} onClick={submit} data-testid="promo-submit" className="mt-3 rounded-lg bg-brand px-4 py-2.5 text-sm font-bold text-white disabled:opacity-50">Create code</button>
    </div>
  );
}
